import { useCallback, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useMutation } from 'react-query'
import '../_messages.scss'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import avatarPlaceholder from 'assets/images/user_placeholder.png'
import AvatarHolder from 'components/UI/AvatarHolder/AvatarHolder'
import ConfirmModal from 'components/UI/Modal/Confirm/ConfirmModal'
import UserDetails from 'components/UI/Modal/UserDetails/UserDetails'
import { ConversationOptionsDropdown } from 'components-ref/molecules/post/OptionsDropdown/ConversationOptionsDropdown'
import { useModalContext } from 'context/modalContext'
import { useLongPress } from 'helpers/hooks/useLongPress'
import { Icons } from 'helpers/icons'
import { someTimeAgo, someTimeAgoConversations } from 'lib/dayjs'
import { deleteConversation } from 'services/endpoints/message'
import { useUserProfile } from 'store'

type TimeFmt = 'messages' | 'posts'

export const ChatCard = ({ chat }: { chat: any }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const userData = useUserProfile()!
  const { id } = useParams()
  const modalData = useModalContext()
  const [timeFmt, setTimeFmt] = useState<TimeFmt>('messages')
  const [isOptionsOpen, setIsOptionsOpen] = useState(false)

  const otherUser = useMemo(() => {
    const u = chat?.other_users?.[0] ?? chat?.user ?? null
    if (!u) return null

    const avatarUrl =
      (typeof u?.avatar === 'object' && typeof u?.avatar?.url === 'string'
        ? u.avatar.url
        : u?.avatar?.src) || null

    return {
      ...u,
      display_name: u.display_name || u.name || '',
      avatar: {
        url: avatarUrl,
      },
    }
  }, [chat])

  const conversationId = useMemo<string | null>(() => {
    return (
      chat?.conversation?.id ??
      chat?.conversation_id ??
      chat?.id ??
      null
    )
  }, [chat])

  const deleteConversationMutation = useMutation(
    async (cid: string) => await deleteConversation(cid),
    {
      onSuccess: () => {
        if (conversationId && Number(id) === otherUser?.id) {
          navigate('/chat', { replace: true })
        }
      },
    }
  )

const handleOpenUserDetails = () => {
  if (!otherUser) return

  if (userData?.role === 'fan') {
    if (!otherUser.username) return

    modalData.clearModal()
    navigate(`/${otherUser.username}`)
    return
  }

  // Model -> model, but viewer has never subscribed.
  if (
    userData?.role === 'model' &&
    otherUser?.show_fan_options === false
  ) {
    if (!otherUser.username) return

    modalData.clearModal()
    navigate(`/${otherUser.username}`)
    return
  }

  // Model -> fan:
  // show_fan_options === null
  //
  // Model -> model with current/expired subscription:
  // show_fan_options === true
  //
  // Both should open UserDetails.
  modalData.addModal(
    `user-details-${otherUser.id ?? 'unknown'}`,
    <UserDetails
      key={`user-details-${otherUser.id ?? 'unknown'}`}
      user={otherUser}
    />,
    true
  )
}
const lastCreatedAt = useMemo<string | null>(() => {
  return chat?.last_activity_at ?? chat?.created_at ?? null
}, [chat])

  const formattedTime = useMemo(() => {
    if (!lastCreatedAt) return ''
    const dt = new Date(String(lastCreatedAt))
    return timeFmt === 'messages' ? someTimeAgoConversations(dt) : someTimeAgo(dt)
  }, [lastCreatedAt, timeFmt])

  const onTimeClick = useCallback((e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    setTimeFmt(prev => (prev === 'messages' ? 'posts' : 'messages'))
  }, [])

  const handleCardClick = () => {
    if (!otherUser || !conversationId) {
      console.warn('ChatCard missing otherUser or conversationId', { chat })
      return
    }

    if (!chat?.other_users?.length && conversationId) {
      sessionStorage.setItem(`convId-${userData.id}`, String(conversationId))
    }

    navigate(`/chat/${otherUser.id}`, {
      state: {
        otherUserData: otherUser,
        conversationId,
      },
    })
  }

  const handleDeleteConversation = useCallback(() => {
    if (!conversationId) return

    modalData.addModal(
      t('deleteConversation'),
      <ConfirmModal
        body={t('onceYouDeleteYourCopyOfTheConversation')}
        confirmFn={() => {
          deleteConversationMutation.mutate(conversationId)
          modalData.clearModal()
        }}
      />
    )
  }, [conversationId, deleteConversationMutation, modalData, t])

  const longPressHandlers = useLongPress({
    delay: 500,
    moveThreshold: 12,
    onLongPress: () => {
      window.setTimeout(() => {
        setIsOptionsOpen(true)
      }, 0)
    },
  })

  const isActive =
    !!otherUser?.id &&
    !!id &&
    String(otherUser.id) === String(id)

  const avatarImg =
    otherUser?.avatar?.url ||
    chat?.other_users?.[0]?.croppedAvatar?.url ||
    chat?.user?.avatar?.url ||
    avatarPlaceholder

  const displayName =
    (otherUser?.name || otherUser?.display_name) ?? t('deletedUser')

  const username =
    (chat?.other_users?.length > 0 && chat?.other_users[0]?.username) ||
    otherUser?.username ||
    null

  const lastMessageBody =
    chat?.last_message

  return (
    <>
      <ConversationOptionsDropdown
        isOpen={isOptionsOpen}
        setIsOpen={setIsOptionsOpen}
        onDelete={handleDeleteConversation}
        withoutBottomNavigation
      />

      <div
        className={`messages__chatcard ${isActive ? 'active-chat' : ''}`}
        onClick={handleCardClick}
        {...longPressHandlers}
      >
        <div
          className="messages__chatcard__avatar"
          style={{ cursor: 'pointer' }}
          onClick={e => {
            e.stopPropagation()
            handleOpenUserDetails()
          }}
        >
          <AvatarHolder img={avatarImg} size="60" isOnline={true} />
          {!!chat?.other_users?.[0]?.is_premium && <Badge src={Icons.premium_badge} />}
        </div>

        <div className="messages__chatcard__info">
          <p className="messages__chatcard__info__name">
            <span className="messages__chatcard__info__name__n">{displayName}</span>
            {username && (
              <span className="messages__chatcard__info__username">@{username}</span>
            )}
          </p>
          {!isActive && (
            <p className="messages__chatcard__info__message">
              {lastMessageBody}
            </p>
          )}
        </div>

        <div className="messages__chatcard__stats">
          <div
            className={`messages__chatcard__stats__time ${
              chat?.unread_messages === 0
                ? 'messages__chatcard__stats__time_no_margin'
                : ''
            }`}
            title={
              timeFmt === 'messages'
                ? 'Showing message-style time • Click to toggle'
                : 'Showing post-style time • Click to toggle'
            }
            role="button"
            tabIndex={0}
            onClick={onTimeClick}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') onTimeClick(e)
            }}
          >
            {formattedTime}
          </div>

          {chat?.unread_messages > 0 && (
            <div className="messages__chatcard__stats__number">
              {chat.unread_messages}
            </div>
          )}
        </div>
      </div>
    </>
  )
}

const Badge = styled.img`
  width: 1.8rem;
  height: 1.8rem;
  position: absolute;
  border-radius: 100%;
  right: 0.3rem;
  bottom: 0.3rem;
  border: solid 0.3rem white;
`